import React, {Component} from "react";
import PropTypes from "prop-types";
import {connect} from "react-redux";
import {FormattedMessage, injectIntl} from "react-intl";
import {BeatLoader} from 'react-spinners';

// @mui/material components
import {
    Table,
    TableBody,
    TableCell,
    TableRow,
    Chip,
    Checkbox,
    Collapse,
    Divider,
    Select,
    Tabs,
    Tab,
    FormControlLabel,
    MenuItem,
    TextField,
    Input,
    InputBase,
    Tooltip,
    Dialog,
    DialogTitle,
    DialogActions,
    DialogContent,
    List,
    ListItem,
    ListItemText,
    RadioGroup,
    Radio,
    Hidden,
    ButtonGroup,
} from "@mui/material"

// @mui/icons-material
import {
    UnfoldMore,
    NotificationsActive,
    ArrowBackIos,
    KeyboardArrowDown,
    KeyboardArrowRight,
    Delete,
} from "@mui/icons-material";

// core components
import TableHead from "../components/tableHead";
import ListingPagination from "components/Product/Table/ListingPagination";
import Button from "components/button/button"
import Snackbar from "components/snackbar/snackbar";
import GridContainer from "components/grid/gridContainer";
import GridItem from "components/grid/gridItem";
import Card from "components/card/card";
import CardHeader from "components/card/cardHeader";
import CardBody from "components/card/cardBody";
import ListingProductPageHeader from "components/page/ListingProductPageHeader";
import ListingCheckbox, {ListingCheckboxPlaceholder} from "components/Product/Table/ListingCheckbox";
import ListingSelectTotalBanner from "components/Product/Table/ListingSelectTotalBanner";
import ListingTotalCount from "components/Product/Table/ListingTotalCount";
import ListingActionsSelect from "components/Product/Table/ListingActionsSelect";
import ListingExportButton from "components/Product/Table/ListingExportButton";
import ListingEmptyState from "components/Product/Table/ListingEmptyState";
import ListingRowsSkeleton from "components/Product/Table/ListingRowsSkeleton";
import ListingSortButton from "components/Product/Table/ListingSortButton";
import ListingActiveFiltersBar, {getFilterTypeLabel} from "components/Product/Table/ListingActiveFiltersBar";
import ListingArticleCell from "components/Product/Table/ListingArticleCell";
import ListingMobileArticleCell from "components/Product/Table/ListingMobileArticleCell";
import ListingSalesCell, {computeTotalSales} from "components/Product/Table/ListingSalesCell";
import ListingStockCell from "components/Product/Table/ListingStockCell";
import ListingTableCell from "components/Product/Table/ListingTableCell";
import ListingProductImageCell from "components/Product/Table/ListingProductImageCell";
import ListingPriceCell from "components/Product/Table/ListingPriceCell";
import {hydrateSelectedFiltersFromParams} from "components/filter/hydrateSelectedFilters";
import FilterBlock from "components/filter/FilterBlock";
import ItemsPerPageSelect from "components/select/itemsPerPageSelect";
import TotalSelectedMessage from "../components/TotalSelectedMessage";
import TotalSelectedActionMessage from "../components/TotalSelectedActionMessage";

// styles
import listStyle from "assets/jss/views/product/variation/listStyle";
import {primaryColor} from "assets/jss/main";

// icons
import {ReactComponent as Arrow3Icon} from "assets/img/arrow-3-linear.svg";
import {ReactComponent as TaskIcon} from "assets/img/task.svg";
import {ReactComponent as CalendarIcon} from "assets/img/calendar.svg";
import {ReactComponent as CalendarTickIcon} from "assets/img/calendar-tick.svg";
import {ReactComponent as DollarCircleIcon} from "assets/img/dollar-circle.svg";
import {ReactComponent as BoxIcon} from "assets/img/box.svg";
import {ReactComponent as BoxTickIcon} from "assets/img/box-tick.svg";
import {ReactComponent as ReceiptDiscountIcon} from "assets/img/receipt-discount.svg";

// action
import {list, reset, downloadExport} from "actions/variation/list";
import {brandList} from "actions/brand/list";
import {categoryList} from "actions/category/list";
import {genderList} from "actions/gender/list";
import {massUpdate, reset as massUpdateReset} from "actions/variation/massUpdate";
import retailerOptionValuesList from "api/retailerOptionValues/list"

// utils
import {sortOn} from "utils/sortOn";
import {hasAuthorization, Authorizations, hasProductEditionAuthorization} from "utils/authorizations";
import abort from "utils/abort";
import {fetch} from "../../../../../utils/dataAccess";
import {encodePercentageCharacter} from "utils/encodePercentageCharacter";
import {getFirstProductImage} from "utils/getFirstProductImage";
import {stockEasyOffer} from "../../../../../domain/enum/offers";
import {defaultProductListFilters} from "utils/listingDefaultFilters";
import {UserAgentUtils} from "../../../../../utils/useragent";
import {getTranslation} from "../../../../../domain/helpers/translations";
import {withStyles} from "@mui/styles";
import {styled} from "@mui/material/styles";

// checkbox vertically centered next to the image column, as in the design
const ImageAndCheckbox = styled('div')({
    display: 'flex',
    alignItems: 'center',
    gap: '8px'
});
import {VariationFilter} from "components/filter/FilterEnum";

class EnhancedTable extends Component {
    static propTypes = {
        error: PropTypes.string,
        retrieved: PropTypes.object,
        loading: PropTypes.bool,
        list: PropTypes.func,
        brandList: PropTypes.func,
        categoryList: PropTypes.func,
        genderList: PropTypes.func,
        massUpdate: PropTypes.func,
        retailerOptionValuesList: PropTypes.func,
    };

    constructor(props) {
        super(props);

        this.state = {
            itemsPerPage: '50',
            page: 1,
            selected: [],
            filterMenuSelected: "StatusStock",
            selectedFilters: [],
            minPriceFilter: 0,
            maxPriceFilter: 0,
            action: "",
            addCategoriesDialog: false,
            deleteCategoriesDialog: false,
            deleteProductsDialog: false,
            genderDialog: false,
            brandDialog: false,
            categoriesToAdd: [],
            categoriesToDelete: [],
            genderSelected: '',
            brandSelected: '',
            showNotification: true,
            searchCategories: '',
            searchBrand: '',
            showChildren: [],
            childrenColumns: [],
            isSelectAllChecked: false,
            isTotalSelected: false,
            totalProductSelected: null,
            loadingAllProduct : false,
            allGendersFetched : false,
            allBrandsFetched : false,
            allCategoriesFetched : false,
            allGenders : [],
            allBrands : [],
            allCategories : [],
            baseParamsUrl: '/retailers/show/',
            retailerOptionValuesList: null,
        };

        this.handleChangeAction = this.handleChangeAction.bind(this);
        this.hydratedFilterSources = new Set();
    };

    // rebuilds the active filter chips from the url after a refresh, once each filter source is loaded
    hydrateSelectedFiltersFromUrl = (props) => {
        const {intl} = this.props;

        const lockFilters = Object.values(VariationFilter.lock.values).map((value) => ({
            "@type": VariationFilter.lock.type,
            "id": value,
            "name": intl.formatMessage({id: `retailer.product.list.filter.lock.${value}`}),
            "value": value
        }));

        const allSources = {
            brands: props.brandRetrieved ? props.brandRetrieved : null,
            genders: props.genderRetrieved ? props.genderRetrieved : null,
            categories: props.categoryRetrieved ? props.categoryRetrieved : null,
            options: this.state.retailerOptionValuesList ? Object.values(this.state.retailerOptionValuesList).flat() : null,
            statuses: [
                {"@type": "Status", "id": "actif", "name": intl.formatMessage({id: 'retailer.product.list.filter.status.yes'}), "value": true},
                {"@type": "Status", "id": "inactif", "name": intl.formatMessage({id: 'retailer.product.list.filter.status.no'}), "value": false},
            ],
            stocks: [
                {"@type": "Stock", "id": "full", "name": intl.formatMessage({id: 'retailer.product.list.filter.stocks.in'}), "value": "full"},
                {"@type": "Stock", "id": "out", "name": intl.formatMessage({id: 'retailer.product.list.filter.stocks.out'}), "value": "out"},
                {"@type": "Stock", "id": "middle", "name": intl.formatMessage({id: 'retailer.product.list.filter.stocks.middle'}), "value": "middle"},
            ],
            validated: [
                {"@type": "Validated", "id": "validated", "name": intl.formatMessage({id: 'retailer.product.list.filter.validated.yes'}), "value": true},
                {"@type": "Validated", "id": "noValidated", "name": intl.formatMessage({id: 'retailer.product.list.filter.validated.no'}), "value": false},
            ],
            eshopIds: [
                {"@type": "EshopId", "id": "withEshopId", "name": intl.formatMessage({id: 'retailer.product.list.filter.eshopid.yes'}), "value": true},
                {"@type": "EshopId", "id": "withoutEshopId", "name": intl.formatMessage({id: 'retailer.product.list.filter.eshopid.no'}), "value": false},
            ],
            locks: lockFilters,
            withPrice: true,
        };

        const sources = {};
        Object.keys(allSources).forEach((key) => {
            if (allSources[key] && !this.hydratedFilterSources.has(key)) {
                sources[key] = allSources[key];
                this.hydratedFilterSources.add(key);
            }
        });

        if (Object.keys(sources).length === 0) {
            return;
        }

        const hydratedFilters = hydrateSelectedFiltersFromParams(decodeURIComponent(props.match.params.params ?? ""), sources);

        if (hydratedFilters.length > 0) {
            const priceFilter = hydratedFilters.find((filter) => filter["@type"] === "Price");

            this.setState((prevState) => ({
                selectedFilters: [
                    ...prevState.selectedFilters,
                    ...hydratedFilters.filter((filter) => !prevState.selectedFilters.some((selected) => selected.id === filter.id && selected["@type"] === filter["@type"])),
                ],
                ...(priceFilter && {minPriceFilter: priceFilter.minPrice, maxPriceFilter: priceFilter.maxPrice}),
            }));
        }
    };

    componentDidMount() {
        const {list} = this.props;

        if (this.props.offer === stockEasyOffer) {
            this.setState({ baseParamsUrl: '/stockeasy/show/retailers/' })
        }

        let retailerOptionValuesOrdered = [];
        let retailer = decodeURIComponent(this.props.match.params.id);
        retailerOptionValuesList(`pagination=false&optionUsedOnVariation=true&retailer_id=${retailer.replace("/retailers/", "")}`)
            .then(retailerOptions => {
                Object.keys(retailerOptions).forEach(function(optionValue) {
                    const optionName = getTranslation(retailerOptions[optionValue]['option'] ?? {translations: []})?.name;
                    if (!retailerOptionValuesOrdered[optionName]) {
                        retailerOptionValuesOrdered[optionName] = [];
                    }
                    retailerOptionValuesOrdered[optionName].push(retailerOptions[optionValue]);
                });
            })
            .finally(() => this.setState({retailerOptionValuesList: retailerOptionValuesOrdered}, () => this.hydrateSelectedFiltersFromUrl(this.props)));

        if (this.props.match.params.params) {
            let params = decodeURIComponent(encodePercentageCharacter(this.props.match.params.params)).split("&");

            this.updateStoreParameters(params);

            list(decodeURIComponent(encodePercentageCharacter(this.props.match.params.params)));

            this.loadSourcesOfUrlFilters();
            this.hydrateSelectedFiltersFromUrl(this.props);
        }
    };

    // brands / genders / categories are lazy-loaded on tab click: when the url contains such
    // filters, fetch them so the active filter chips can be hydrated after a refresh
    loadSourcesOfUrlFilters = () => {
        const params = decodeURIComponent(this.props.match.params.params ?? "");
        const retailer = decodeURIComponent(this.props.match.params.id);

        if (params.includes("brand.id=") && !this.props.brandRetrieved) {
            this.props.brandList(`pagination=false&onlyExistingProducts=true&retailer=${retailer.split("/retailers/").pop()}`);
        }

        if (params.includes("gender.id=") && !this.props.genderRetrieved) {
            this.props.genderList(`pagination=false&onlyExistingProducts=true&retailer=${retailer.split("/retailers/").pop()}`);
        }

        if (params.includes("categories.id=") && !this.props.categoryRetrieved) {
            this.props.categoryList(`pagination=false&onlyExistingProducts=true&retailer=${retailer.split("/retailers/").pop()}`);
        }
    };

    UNSAFE_componentWillReceiveProps(nextProps) {
        this.hydrateSelectedFiltersFromUrl(nextProps);

        if (this.props.massUpdateSuccess !== nextProps.massUpdateSuccess) {
            this.props.list(decodeURIComponent(this.props.match.params.params));

            this.setState({
                loadingAllProduct: false
            });

            this.props.massUpdateReset();
        }

        if (this.props.match.params.params !== nextProps.match.params.params) {
            window.scrollTo(0, 0);

            let params = decodeURIComponent(encodePercentageCharacter(nextProps.match.params.params)).split("&");

            let newParams = [];

            for(let param of params){
                let tmpValues = param.split("=");

                newParams.push(tmpValues[0] + "=" + encodeURIComponent(tmpValues[1]));
            }

            nextProps.list(
                nextProps.match.params.params &&
                newParams.join('&')
            );

            this.setState({
                isSelectAllChecked: false,
                selected: [],
                isTotalSelected: false,
                totalProductSelected: null
            });

            // reset filters when reload page
            this.updateStoreParameters(params, params.length === 3);
        }
    };

    componentWillUnmount() {
        this.props.reset();
        abort.abortRequests();
    }

    resetSelectedAndCloseDialogAndRefreshList = (dialogName = false) => {
        this.setState({
            selected: [],
            isSelectAllChecked: false
        });

        if (dialogName) {
            this.handleDialog(dialogName);
        }

        this.props.list(decodeURIComponent(this.props.match.params.params));
    };

    updateStoreParameters(params, resetFilters = false) {
        params.map((param) => {
            return this.setState({[param.split("=")[0]]: param.split("=")[1]})
        });

        if (resetFilters) {
            this.setState({selectedFilters: []})
        }
    }

    getCategoriesByVariations = (variationsArray) => {
        let categoriesArray = [];

        variationsArray.forEach(variation => {
            variation.product.categories.forEach(cat => {
                categoriesArray.push(cat);
            })
        });

        return this.groupCategories(categoriesArray);
    };

    groupCategories = (categoriesArray) => {
        let uniqueCatArray = [];

        // check if the parent of variation is already in the array
        categoriesArray.forEach(cat => {
            let isCatAlreadyAdded = uniqueCatArray.some(item => item.id === cat.id)
            if (!isCatAlreadyAdded) {
                uniqueCatArray.push(cat);
            }
        });

        return uniqueCatArray.sort(sortOn("name"));
    };

    // duplicate in filterMenu.js
    checkIfFilterExist = (element) => {
        const {selectedFilters} = this.state;

        for (let i = 0; i < selectedFilters.length; i++) {
            if (selectedFilters[i].id === element.id) {
                return true
            }
        }

        return false;
    };

    handleRouteReset() {
        this.setState({name: ""});
        this.props.history.push(`${this.state.baseParamsUrl + this.props.match.params.id}/products/variations/${encodeURIComponent(`retailer.id=${decodeURIComponent(this.props.match.params.id).replace("/retailers/", "")}&itemsPerPage=${this.state.itemsPerPage}&page=1`)}`);
    }

    handlePageReset = (params) => {
        params = params.split("&");

        params.map((param, key) => {
            if (param.search("page") !== -1) {
                params[key] = "page=1";
            }

            return true;
        });

        return params;
    };

    isCatalogEmpty = () => {
        const {retrieved} = this.props;

        if (!retrieved || retrieved["hydra:totalItems"] !== 0) {
            return false;
        }

        const baseParams = ["retailer.id", "order[", "itemsPerPage", "page", "pagination"];
        const params = decodeURIComponent(this.props.match.params.params ?? "").split("&");

        return params.every((param) => param === "" || baseParams.some((baseParam) => param.startsWith(baseParam)));
    };

    handlePageChange = (page) => {
        let params = decodeURIComponent(encodePercentageCharacter(this.props.match.params.params)).split("&");

        if (params.some((param) => param.search("page") !== -1)) {
            params = params.map((param) => param.search("page") !== -1 ? `page=${page}` : param);
        } else {
            params.push(`page=${page}`);
        }

        this.props.history.push(`${this.state.baseParamsUrl + this.props.match.params.id}/products/variations/${encodeURIComponent(params.join("&"))}`);
    };

    getCurrentSort = () => {
        const orderParam = this.getParameterNameAndPropertyValue(decodeURIComponent(this.props.match.params.params), "order");
        const matches = orderParam.match(/order\[([^\]]+)\]=(asc|desc)/);

        return matches ? {property: matches[1], order: matches[2]} : null;
    };

    handleSortChange = (property) => {
        const currentSort = this.getCurrentSort();
        const defaultOrder = ["updatedAt", "onlineSales", "totalSales", "discountRate"].includes(property) ? "desc" : "asc";
        const order = currentSort?.property === property
            ? ("asc" === currentSort.order ? "desc" : "asc")
            : defaultOrder;

        const params = this.handlePageReset(decodeURIComponent(this.props.match.params.params))
            .filter((param) => param.search("order") === -1);

        if (property === "outletPrice") {
            params.push(`order[discountedPrice]=${order}`, `order[outletPrice]=${order}`);
        } else {
            params.push(`order[${property}]=${order}`);
        }

        this.props.history.push(`${this.state.baseParamsUrl + this.props.match.params.id}/products/variations/${encodeURIComponent(params.join("&"))}`);
    };

    getSortOptions = () => [
        {value: "product.id", labelId: "retailer.product.list.sort.ranking", icon: Arrow3Icon},
        {value: "translations.name", labelId: "retailer.product.list.sort.name", icon: TaskIcon},
        {value: "updatedAt", labelId: "retailer.product.list.sort.updatedAt", icon: CalendarIcon},
        {value: "manualUpdatedAt", labelId: "retailer.product.list.sort.manualUpdatedAt", icon: CalendarTickIcon},
        {value: "outletPrice", labelId: "retailer.product.list.sort.price", icon: DollarCircleIcon},
        {value: "discountRate", labelId: "retailer.product.list.sort.promotion", icon: ReceiptDiscountIcon},
        {value: "onlineSales", labelId: "retailer.product.list.sales.online", icon: BoxIcon},
        {value: "totalSales", labelId: "retailer.product.list.sales.total", icon: BoxTickIcon},
    ];

    renderSortButton = () => (
        <ListingSortButton
            options={this.getSortOptions()}
            // ranking is the endpoint's default order when no sort is selected
            activeValue={this.getCurrentSort()?.property ?? "product.id"}
            onSelect={this.handleSortChange}
        />
    );

    handleSearch = (event) => {
        abort.abortRequests();

        this.setState({name: event.target.value});

        let params = this.handlePageReset(decodeURIComponent(encodePercentageCharacter(this.props.match.params.params)));
        let newParams = "";

        if (event.target.value.length > 0) {
            let search = encodePercentageCharacter(event.target.value);

            newParams = `translations.name=${search}`;
        }

        params.map((param) => {
            if (param.search("translations.name=") === -1) {
                newParams += `&${param}`;
            }

            return true;
        });

        this.props.history.push(`${this.state.baseParamsUrl + this.props.match.params.id}/products/variations/${encodeURIComponent(newParams)}`);
    };

    handleAddSelectedFilter = element => {
        if (!this.checkIfFilterExist(element)) {
            this.setState({selectedFilters: [...this.state.selectedFilters, element]}, () => this.handleUpdateAddFilter(element, this.state.selectedFilters));
        }
    };

    // duplicate in filterMenu.js
    handleUpdateAddFilter(element, selectedFilters) {
        let params = this.handlePageReset(decodeURIComponent(this.props.match.params.params));
        let newParams = "";

        // this prevents duplication of filter in url
        params.map((param) => {
            if ((param.search("stock") === -1 || element["@type"] !== "Stock") &&
                (param.search("status") === -1 || element["@type"] !== "Status") &&
                (param.search("validated") === -1 || element["@type"] !== "Validated") &&
                (param.search("eshopId") === -1 || element["@type"] !== "EshopId") &&
                (param.search("categories") === -1 || element["@type"] !== "Category") &&
                (param.search("brand") === -1 || element["@type"] !== "Brand") &&
                (param.search("gender") === -1 || element["@type"] !== "Gender") &&
                (param.search("options") === -1 || element["@type"] !== "RetailerOptionValue") &&
                (param.search(VariationFilter.lock.property) === -1 || element["@type"] !== VariationFilter.lock.type)) {
                newParams += `${param}&`;
            }

            return true;
        });

        switch (element["@type"]) {
            case "Brand":
                let newBrands = [];
                selectedFilters.filter(filter => filter["@type"] === "Brand").forEach(brand => {
                    newBrands.push(`"${brand.id}"`);
                });
                newParams += `brand.id=[${newBrands}]`;
                break;
            case "Category":
                let newCategories = [];
                selectedFilters.filter(filter => filter["@type"] === "Category").forEach(category => {
                    newCategories.push(category.id);
                });
                newParams += `categories.id={${newCategories.toString()}}`;
                break;
            case "Gender":
                let newGenders = [];
                selectedFilters.filter(filter => filter["@type"] === "Gender").forEach(gender => {
                    newGenders.push(`"${gender.id}"`);
                });
                newParams += `gender.id=[${newGenders}]`;
                break;
            case "Status":
                newParams += `status=${element.value}`;
                selectedFilters.map((filter) => {
                    if (filter["@type"] === "Status" && element.value !== filter["value"]) {
                        let oldStockFilterIndex = selectedFilters.indexOf(filter);
                        selectedFilters.splice(oldStockFilterIndex, 1);
                        this.setState({selectedFilters: selectedFilters});
                    }

                    return true;
                });

                break;
            case "Validated":
                newParams += `validated=${element.value}`;
                selectedFilters.map((filter) => {
                    if (filter["@type"] === "Validated" && element.value !== filter["value"]) {
                        let oldValidatedFilterIndex = selectedFilters.indexOf(filter);
                        selectedFilters.splice(oldValidatedFilterIndex, 1);
                        this.setState({selectedFilters: selectedFilters});
                    }

                    return true;
                });

                break;
            case "EshopId":
                newParams += `exists[eshopId]=${element.value}`;
                selectedFilters.map((filter) => {
                    if (filter["@type"] === "EshopId" && element.value !== filter["value"]) {
                        let oldEshopIdFilterIndex = selectedFilters.indexOf(filter);
                        selectedFilters.splice(oldEshopIdFilterIndex, 1);
                        this.setState({selectedFilters: selectedFilters});
                    }

                    return true;
                });

                break;
            case "Stock":
                if (element.value === "out") {
                    newParams += "stock[lte]=0";
                } else if (element.value === "middle") {
                    newParams += "stock[gt]=0&stock[lte]=5";
                } else {
                    newParams += "stock[gt]=0";
                }
                selectedFilters.map((filter) => {
                    if (filter["@type"] === "Stock" && element.value !== filter["value"]) {
                        let oldStockFilterIndex = selectedFilters.indexOf(filter);
                        selectedFilters.splice(oldStockFilterIndex, 1);
                        this.setState({selectedFilters: selectedFilters});
                    }

                    return true;
                });

                break;
            case VariationFilter.lock.type:
                const selectedLockFilters = selectedFilters.filter(filter => filter["@type"] === VariationFilter.lock.type);
                const combinedFilters = [VariationFilter.lock.values.ALL, VariationFilter.lock.values.VARIATION];

                let lockFiltersToRemove = [];
                let newLockFilter;

                if (combinedFilters.includes(element.value)) {
                    // if the filter is a combined filter
                    // keep only the new selected lock filter as the only lock filter
                    lockFiltersToRemove = selectedLockFilters.filter(filter => element.value !== filter["value"]);

                    newLockFilter = element.value;
                } else {
                    // remove combined lock filter
                    lockFiltersToRemove = selectedLockFilters.filter(filter => combinedFilters.includes(filter["value"]))

                    newLockFilter = selectedLockFilters.map(lock => lock.value);
                }

                if (newLockFilter) {
                    newParams += `${VariationFilter.lock.property}=${newLockFilter}`;
                }

                this.removeFiltersFromSelectedFilters(lockFiltersToRemove)
                break;
            case "RetailerOptionValue":
                let newOptions = [];
                selectedFilters.filter(filter => filter["@type"] === "RetailerOptionValue").forEach(option => {
                    newOptions.push(`"${option.id}"`);
                });
                newParams += `options.id=[${newOptions}]`;
                break;
            default:
                newParams = ""
        }

        this.props.history.push(`${this.state.baseParamsUrl + this.props.match.params.id}/products/variations/${encodeURIComponent(newParams)}`);
    }

    // remove several filters at once
    // duplicate in filterMenu.js
    removeFiltersFromSelectedFilters(filters) {
        const {selectedFilters} = this.state;

        if (filters.length === 0) {
            return;
        }

        filters.forEach((filter) => {
            let oldFilterIndex = selectedFilters.indexOf(filter);
            selectedFilters.splice(oldFilterIndex, 1);
        });

        this.setState({selectedFilters: selectedFilters});
    }

    // remove one filter at once
    // duplicate in filterMenu.js
    removeFilterValueFromSelectedFilters(filter) {
        const {selectedFilters} = this.state;

        let oldFilterIndex = selectedFilters.indexOf(filter);
        selectedFilters.splice(oldFilterIndex, 1);

        this.setState({selectedFilters: selectedFilters});
    }

    // duplicate in filterMenu.js
    handleDeleteSelectedFilter = element => {
        let selectedFilters = this.state.selectedFilters;

        if (this.checkIfFilterExist(element)) {
            let index = selectedFilters.indexOf(element);

            selectedFilters.splice(index, 1);
            this.setState({selectedFilters});

            let params = this.handlePageReset(decodeURIComponent(this.props.match.params.params));
            let newParams = "";

            // this prevents duplication of filter in url
            params.map((param) => {
                if ((param.search("stock") === -1 || element["@type"] !== "Stock") &&
                    (param.search("status") === -1 || element["@type"] !== "Status") &&
                    (param.search("validated") === -1 || element["@type"] !== "Validated") &&
                    (param.search("eshopId") === -1 || element["@type"] !== "EshopId") &&
                    (param.search("brand") === -1 || element["@type"] !== "Brand") &&
                    (param.search("options") === -1 || element["@type"] !== "RetailerOptionValue") &&
                    (param.search("gender") === -1 || element["@type"] !== "Gender") &&
                    (param.search("categories") === -1 || element["@type"] !== "Category") &&
                    (param.search(VariationFilter.lock.property) === -1 || element["@type"] !== VariationFilter.lock.type)) {

                    newParams += `${param}&`;
                }

                // but all the code above duplicates the urls :(
                // all these if conditions must be at the same level as the 'Lock' filter.
                // this will be fixed later
                if (param.search("categories") >= 0 && element["@type"] === "Category") {
                    let newCategories = [];
                    selectedFilters.filter(filter => filter["@type"] === "Category").forEach(category => {
                        newCategories.push(category.id);
                    });
                    newParams += `categories.id={${newCategories.toString()}}&`
                }

                if (param.search("gender") >= 0 && element["@type"] === "Gender") {
                    let newGenders = [];
                    selectedFilters.filter(filter => filter["@type"] === "Gender").forEach(gender => {
                        newGenders.push(`"${gender.id}"`);
                    });
                    newParams += `gender.id=[${newGenders}]&`;
                }

                if (param.search("brand") >= 0 && element["@type"] === "Brand") {
                    let newBrands = [];
                    selectedFilters.filter(filter => filter["@type"] === "Brand").forEach(brand => {
                        newBrands.push(`"${brand.id}"`);
                    });
                    newParams += `brand.id=[${newBrands}]&`;
                }

                if (param.search("option") >= 0 && element["@type"] === "RetailerOptionValue") {
                    let newOptions = [];
                    selectedFilters.filter(filter => filter["@type"] === "RetailerOptionValue").forEach(option => {
                        newOptions.push(`"${option.id}"`);
                    });
                    newParams += `options.id=[${newOptions}]&`;
                }

                return true;
            });

            if (element["@type"] === VariationFilter.lock.type) {
                const newLockFilter = selectedFilters
                    .filter(filter => filter["@type"] === VariationFilter.lock.type)
                    .map(lock => lock.value)
                ;

                newParams += `${VariationFilter.lock.property}=${newLockFilter}&`;
            }

            if (newParams.substr(newParams.length - 1) === "&") {
                newParams = newParams.substring(0, newParams.length - 1);
            }

            this.props.history.push(`${this.state.baseParamsUrl + this.props.match.params.id}/products/variations/${encodeURIComponent(newParams)}`);
        }
    };

    handlePriceFilter = () => {
        const {minPriceFilter, maxPriceFilter, selectedFilters} = this.state;

        let params = this.handlePageReset(decodeURIComponent(this.props.match.params.params));
        let newParams = "";

        params.map((param) => {
            if (param.search("outletPrice") === -1 && param.search("between") === -1) {
                newParams += `${param}&`;
            }

            return true;
        });

        // string to number
        let minPrice = Number(minPriceFilter);
        let maxPrice = Number(maxPriceFilter);

        const newPriceFilter = {
            "@type": "Price",
            "minPrice": minPrice,
            "maxPrice": maxPrice
        };

        let newSelectedFilters = selectedFilters;

        // check if a price filter exist
        newSelectedFilters.forEach(element => {
            if (element["@type"] === "Price") {

                // remove price filter
                let index = newSelectedFilters.indexOf(element);
                newSelectedFilters.splice(index, 1);
                this.setState({selectedFilters: newSelectedFilters})
            }
        });

        // create price filter
        if (minPrice < maxPrice) {
            newParams += `outletPrice[between]=${newPriceFilter.minPrice}..${newPriceFilter.maxPrice}`;

            newPriceFilter.maxPrice = newPriceFilter.maxPrice.toLocaleString();
            newPriceFilter.minPrice = newPriceFilter.minPrice.toLocaleString();

            this.setState({selectedFilters: [...selectedFilters, newPriceFilter]});
            this.props.history.push(`${this.state.baseParamsUrl + this.props.match.params.id}/products/variations/${encodeURIComponent(newParams)}`);
        }
    };

    handleChangeAction = event => {
        switch (event.target.value) {
            case "active":
                this.handleEditStatusToSelected(true);
                break;
            case "inactive":
                this.handleEditStatusToSelected(false);
                break;
            case "addCategories":
                this.handleDialog("addCategoriesDialog");
                break;
            case "deleteCategories":
                this.handleDialog('deleteCategoriesDialog');
                break;
            case "editGender":
                this.handleDialog("genderDialog");
                break;
            case "editBrand":
                this.handleDialog("brandDialog");
                break;
            case "deleteProducts":
                this.handleDialog('deleteProductsDialog');
                break;
            default:
        }
    };

    handleSelectAllClick = event => {
        const {retrieved} = this.props;
        if (event.target.checked) {
            let filteredProducts = retrieved["hydra:member"];

            if (this.props.offer === stockEasyOffer) {
                filteredProducts = this.removeEikowAndFeedProductsFromList(retrieved["hydra:member"])
            }

            let newDataArray = JSON.parse(JSON.stringify(filteredProducts));

            this.setState({
                selected: newDataArray,
                isSelectAllChecked: true,
                totalProductSelected: filteredProducts.length
            });
        } else {
            this.setState({
                selected: [],
                isTotalSelected: false,
                isSelectAllChecked: false
            });
        }
    };

    handleSelectProduct = item => {
        const {selected} = this.state;

        let isItemAlreadyAdded = selected.some(itemAdded => itemAdded.id === item.id);

        if (!isItemAlreadyAdded) {
            this.setState({
                selected: [...selected, item]
            });
        } else {
            let newSelected = selected;

            for(let i = 0; i < newSelected.length; i++) {
                if (newSelected[i].id === item.id) {
                    newSelected.splice(i, 1);
                }
            }

            this.setState({
                selected: newSelected
            });
        }
    };

    handleCheckCategoryAdd = value => {
        const {categoriesToAdd} = this.state;
        const currentIndex = categoriesToAdd.indexOf(value);
        const newChecked = [...categoriesToAdd];

        if (currentIndex === -1) {
            newChecked.push(value);
        } else {
            newChecked.splice(currentIndex, 1);
        }

        this.setState({
            categoriesToAdd: newChecked
        });
    };

    handleCheckCategoryDelete = value => {
        const {categoriesToDelete} = this.state;
        const currentIndex = categoriesToDelete.indexOf(value);
        const newChecked = [...categoriesToDelete];

        if (currentIndex === -1) {
            newChecked.push(value);
        } else {
            newChecked.splice(currentIndex, 1);
        }

        this.setState({
            categoriesToDelete: newChecked
        });
    };

    handleEditStatusToSelected = (status) => {
        const params = {
            'variationList' : [],
            'key': ['status', status]
        };

        if (!this.state.isTotalSelected) {
            params['variationList'] = this.state.selected.map((item) => item['@id']);

            this.props.massUpdate(params);
        } else {
            this.setState({ loadingAllProduct: true });

            fetch("/variations?" + decodeURIComponent(this.props.match.params.params) + "&itemsPerPage=999999&page=1").then(
                response => {
                    response.json().then(retrieved => {
                        let variationList = retrieved['hydra:member'];

                        if (this.props.offer === stockEasyOffer) {
                            variationList = this.removeEikowAndFeedProductsFromList(variationList);
                        }

                        params['variationList'] = variationList.map((item) => item['@id']);

                        this.props.massUpdate(params);
                    });
                }
            );
        }

        this.setState({
            isTotalSelected: false,
            inputSelectClicked: false
        });

        this.resetSelectedAndCloseDialogAndRefreshList()
    };

    handleAddCategoriesToSelected = () => {
        const params = {
            'variationList' : [],
            'key': ['categoryAdd', this.state.categoriesToAdd]
        };

        if (!this.state.isTotalSelected) {
            params['variationList'] = this.state.selected.map((item) => item['@id']);

            this.props.massUpdate(params);
        } else {
            this.setState({ loadingAllProduct: true });

            fetch("/variations?" + decodeURIComponent(this.props.match.params.params) + "&itemsPerPage=999999&page=1").then(
                response => {
                    response.json().then(retrieved => {
                        params['variationList'] = retrieved['hydra:member'].map((item) => item['@id']);

                        this.props.massUpdate(params);
                    });
                }
            );
        }

        this.setState({ isTotalSelected: false });

        this.resetSelectedAndCloseDialogAndRefreshList("addCategoriesDialog")
    };

    handleDeleteCategoriesToSelected = () => {
        const params = {
            'variationList' : [],
            'key': ['categoryDelete', this.state.categoriesToDelete]
        };

        if (!this.state.isTotalSelected) {
            params['variationList'] = this.state.selected.map((item) => item['@id']);

            this.props.massUpdate(params);
        } else {
            this.setState({ loadingAllProduct: true });

            fetch("/variations?" + decodeURIComponent(this.props.match.params.params) + "&itemsPerPage=999999&page=1").then(
                response => {
                    response.json().then(retrieved => {
                        params['variationList'] = retrieved['hydra:member'].map((item) => item['@id']);

                        this.props.massUpdate(params);
                    });
                }
            );
        }

        this.setState({ isTotalSelected: false });

        this.resetSelectedAndCloseDialogAndRefreshList("deleteCategoriesDialog");
    };

    handleDeleteProductsToSelected = () => {
        const params = {
            'variationList' : [],
            'key': ['variationDelete']
        };

        if (!this.state.isTotalSelected) {
            params['variationList'] = this.state.selected.map((item) => item['@id']);

            this.props.massUpdate(params);
        } else {
            this.setState({ loadingAllProduct: true });

            fetch("/variations?" + decodeURIComponent(this.props.match.params.params) + "&itemsPerPage=999999&page=1").then(
                response => {
                    response.json().then(retrieved => {
                        params['variationList'] = retrieved['hydra:member'].map((item) => item['@id']);

                        this.props.massUpdate(params);
                    });
                }
            );
        }

        this.setState({ isTotalSelected: false });

        this.resetSelectedAndCloseDialogAndRefreshList("deleteProductsDialog");
    };

    handleEditGenderToSelected = (genderId) => {
        const params = {
            'variationList': [],
            'key': ['gender', genderId]
        };

        if (!this.state.isTotalSelected) {
            params['variationList'] = this.state.selected.map((item) => item['@id']);

            this.props.massUpdate(params);
        } else if (this.state.isTotalSelected && "" !== genderId.trim()) {
            this.setState({ loadingAllProduct: true });

            fetch("/variations?" + decodeURIComponent(this.props.match.params.params) + "&itemsPerPage=999999&page=1").then(
                response => {
                    response.json().then(retrieved => {
                        params['variationList'] = retrieved['hydra:member'].map((item) => item['@id']);

                        this.props.massUpdate(params);
                    });
                }
            );
        }

        this.setState({ isTotalSelected: false });

        this.resetSelectedAndCloseDialogAndRefreshList("genderDialog")
    };

    handleEditBrandToSelected = (brandId) => {
        const params = {
            'retailer': decodeURIComponent(this.props.match.params.id),
            'variationList': [],
            'key': ['brand', brandId]
        };

        if (!this.state.isTotalSelected) {
            params['variationList'] = this.state.selected.map((item) => item['@id']);

            this.props.massUpdate(params);
        } else if (this.state.isTotalSelected && "" !== brandId.trim()) {
            this.setState({ loadingAllProduct: true });

            fetch("/variations?" + decodeURIComponent(this.props.match.params.params) + "&itemsPerPage=999999&page=1").then(
                response => {
                    response.json().then(retrieved => {
                        params['variationList'] = retrieved['hydra:member'].map((item) => item['@id']);

                        this.props.massUpdate(params);
                    });
                }
            );
        }

        this.setState({ isTotalSelected: false });

        this.resetSelectedAndCloseDialogAndRefreshList("brandDialog")
    };

    handleDialog = (dialogName) => {
       if ('genderDialog' === dialogName && !this.state.allGendersFetched) {
            fetch(`/genders?pagination=false&organization.id=${this.props.currentOrganization}`).then(
                response => {
                    response.json().then(retrieved => {
                        this.setState({ allGenders: retrieved['hydra:member'], allGendersFetched: true })
                    });
                }
            );
        } else if ('brandDialog' === dialogName && !this.state.allBrandsFetched) {
            fetch(`/brands?pagination=false&organization.id=${this.props.currentOrganization}`).then(
                response => {
                    response.json().then(retrieved => {
                        this.setState({ allBrands: retrieved['hydra:member'], allBrandsFetched: true })
                    });
                }
            );
        } else if ('addCategoriesDialog' === dialogName && !this.state.allCategoriesFetched) {
            fetch(`/categories?pagination=false&organization.id=${this.props.currentOrganization}`).then(
                response => {
                    response.json().then(retrieved => {
                        this.setState({allCategories: retrieved['hydra:member'], allCategoriesFetched: true })
                    });
                }
            );
        }

        this.setState({
            [dialogName]: !this.state[dialogName],
            searchCategories: '',
            categoriesToAdd: []
        });
    };

    renderArticleCell = (variation) => (
        <ListingArticleCell
            name={getTranslation(variation).name}
            isEikow={variation.product.eikow}
            brandName={variation.product.brand?.name}
            sku={variation.sku}
            eshopId={variation.eshopId}
            categories={(variation.product.categories ?? [])
                .map((category) => ({id: category.id, name: getTranslation(category).name, draft: category.draft}))
                .sort(sortOn("name"))}
            updatedAt={variation.updatedAt}
            manualUpdatedAt={variation.manualUpdatedAt}
            manualUpdatedBy={variation.manualUpdatedBy ?? variation.manualUpdatedByName}
        />
    );

    // below the sm breakpoint the row keeps two columns and this cell stacks
    // what the hidden price / sales / stock columns carry
    renderMobileArticleCell = (variation) => {
        const currentPrice = variation.discountedPrice ?? variation.outletPrice ?? variation.price;
        const hasDiscount = (variation.discountedPrice || variation.outletPrice) && currentPrice !== variation.price;
        const optionsLabel = (variation.retailerOptionValues ?? [])
            .map((optionValue) => getTranslation(optionValue ?? {translations: []})?.name)
            .filter(Boolean)
            .join(' · ');

        return (
            <ListingMobileArticleCell
                name={getTranslation(variation).name}
                isEikow={variation.product.eikow}
                brandName={variation.product.brand?.name}
                sku={variation.sku}
                active={variation.status}
                validated={variation.validated}
                validationErrors={variation.validationErrors}
                price={currentPrice}
                previousPrice={hasDiscount ? variation.price : null}
                currency={this.props.currentOrganization?.currency}
                priceCompetitiveness={variation.priceCompetitiveness}
                totalSales={computeTotalSales(variation)}
                stocks={[{id: variation.id, label: optionsLabel, stock: variation.stock ?? 0}]}
            />
        );
    };

    renderStockCell = (variation) => {
        const optionsLabel = (variation.retailerOptionValues ?? [])
            .map((optionValue) => getTranslation(optionValue ?? {translations: []})?.name)
            .filter(Boolean)
            .join(' · ');

        return (
            <ListingStockCell
                active={variation.status}
                validated={variation.validated}
                validationErrors={variation.validationErrors}
                siteUrl={variation.status ? variation.url : null}
                stocks={[{id: variation.id, label: optionsLabel, stock: variation.stock ?? 0}]}
            />
        );
    };

    renderPriceCell = (variation) => {
        const currentPrice = variation.discountedPrice ?? variation.outletPrice ?? variation.price;
        const hasDiscount = (variation.discountedPrice || variation.outletPrice) && currentPrice !== variation.price;

        return (
            <ListingPriceCell
                price={currentPrice}
                previousPrice={hasDiscount ? variation.price : null}
                currency={this.props.currentOrganization?.currency}
                priceCompetitiveness={variation.priceCompetitiveness}
            />
        );
    };

    renderSelectTotal() {
        const {classes, retrieved} = this.props;
        const {isTotalSelected, itemsPerPage, totalProductSelected, selected, isSelectAllChecked} = this.state;

        if (!retrieved || !isSelectAllChecked) {
            return null;
        }

        const editableVariations = this.removeEikowAndFeedProductsFromList(retrieved['hydra:member'])

        // if all possible editable product are selected
        if (selected.length === editableVariations.length && retrieved['hydra:member'].length === retrieved['hydra:totalItems']) {
            return null;
        }

        return (
            <ListingSelectTotalBanner
                message={
                    <TotalSelectedMessage
                        offer={this.props.offer}
                        totalProductSelected={totalProductSelected}
                        isTotalSelected={isTotalSelected}
                    />
                }
                actionLabel={
                    <TotalSelectedActionMessage
                        offer={this.props.offer}
                        totalItems={retrieved["hydra:totalItems"]}
                        isTotalSelected={isTotalSelected}
                    />
                }
                onAction={() => {
                    if (!isTotalSelected) {
                        this.setState({
                            isTotalSelected: true,
                            totalProductSelected: retrieved["hydra:totalItems"]
                        })
                    } else {
                        this.setState({
                            isTotalSelected: false,
                            selected: [],
                            isSelectAllChecked: false,
                            totalProductSelected: parseInt(itemsPerPage)
                        })
                    }
                }}
            />
        )
    }

    // duplicate in filterMenu.js
    renderFilterMenu = () => {
        const {classes, brandRetrieved, categoryRetrieved, genderRetrieved, offer, intl} = this.props;
        const {retailerOptionValuesList} = this.state;
        const {filterMenuSelected} = this.state;

        if (null === retailerOptionValuesList) {
            return;
        }

        const statusRetrieved = [
            {"@type": "Status", "id": "actif", "name": intl.formatMessage({id:'retailer.product.list.filter.status.yes'}), "value": true},
            {"@type": "Status", "id": "inactif", "name": intl.formatMessage({id:'retailer.product.list.filter.status.no'}), "value": false}
        ];

        const stockRetrieved = [
            {"@type": "Stock", "id": "full", "name": intl.formatMessage({id:'retailer.product.list.filter.stocks.in'}), "value": "full"},
            {"@type": "Stock", "id": "out", "name": intl.formatMessage({id:'retailer.product.list.filter.stocks.out'}), "value": "out"},
            {"@type": "Stock", "id": "middle", "name": intl.formatMessage({id:'retailer.product.list.filter.stocks.middle'}), "value": "middle"},
        ];

        const validatedRetrieved = [
            {"@type": "Validated", "id": "validated", "name": intl.formatMessage({id:'retailer.product.list.filter.validated.yes'}), "value": true},
            {"@type": "Validated", "id": "noValidated", "name": intl.formatMessage({id:'retailer.product.list.filter.validated.no'}), "value": false},
        ];

        const eshopIdRetrieved = [
            {"@type": "EshopId", "id": "withEshopId", "name": intl.formatMessage({id:'retailer.product.list.filter.eshopid.yes'}), "value": true},
            {"@type": "EshopId", "id": "withoutEshopId", "name": intl.formatMessage({id:'retailer.product.list.filter.eshopid.no'}), "value": false},
        ];

        const categoryRetrievedSorted = categoryRetrieved ? categoryRetrieved.sort(sortOn("name")) : false;
        const brandRetrievedSorted = brandRetrieved ? brandRetrieved.sort(sortOn("name")) : false;

        let filters={};

        if (statusRetrieved && statusRetrieved.length > 0) {
            let filtersSource;

            // we don't display status / status when it is Stock Easy
            if (stockEasyOffer === offer) {
                filtersSource = {
                    "StatusStock": [{"Validated": validatedRetrieved}, {"Stocks": stockRetrieved}],
                }
            } else {
                filtersSource = {
                    "StatusStock": [{"Status": statusRetrieved}, {"Validated": validatedRetrieved}, {'EshopId': eshopIdRetrieved}, {"Stocks": stockRetrieved}],
                }
            }

            Object.assign(filters, filtersSource);
        }

        const lockFilter = [];

        for (let [key, value] of Object.entries(VariationFilter.lock.values)) {
            lockFilter.push({
                "@type":  VariationFilter.lock.type,
                "id": value,
                "name": intl.formatMessage({id: `retailer.product.list.filter.lock.${value}`}),
                "value": value
            });
        }

        Object.assign(filters,  {"Brand": brandRetrievedSorted});
        Object.assign(filters,  {"Gender": genderRetrieved && genderRetrieved.length > 0 ? genderRetrieved : null});
        Object.assign(filters,  {"Category": categoryRetrievedSorted});
        Object.keys(retailerOptionValuesList).forEach(function(optionName){
            Object.assign(filters, {[optionName]: retailerOptionValuesList[optionName]});
        });
        Object.assign(filters,  {"Price": 0});
        Object.assign(filters, {[VariationFilter.lock.type]: lockFilter});

        return (
            <div>
                <div className={classes.filterMenu}>
                    <Tabs
                        variant="scrollable"
                        value={Object.keys(filters).indexOf(filterMenuSelected)}
                        classes={{root: classes.tabsRoot, indicator: classes.tabsIndicator}}
                    >
                        {Object.keys(filters).map(name => {
                            return (
                                <Tab
                                    key={name}
                                    onClick={
                                        () => {
                                            this.setState({filterMenuSelected: name});
                                            this.handleTabFilterChange(name);
                                        }
                                    }
                                    disableRipple
                                    classes={{root: classes.tabRoot, selected: classes.tabSelected}}
                                    label={<FormattedMessage id={"retailer.product.list.filter." + name.toLowerCase()} defaultMessage={name}/>}
                                />
                            )
                        })}
                    </Tabs>
                </div>
                <div className={classes.filters}>
                    {this.renderFilterElement(filters)}
                </div>
            </div>
        );
    };

    handleTabFilterChange = value => {
        let retailer = decodeURIComponent(this.props.match.params.id);

        if ('Brand' === value && !this.props.brandRetrieved) {
            this.props.brandList(`pagination=false&onlyExistingProducts=true&retailer=${retailer.split("/retailers/").pop()}`);
        } else if ('Gender' === value && !this.props.genderRetrieved) {
            this.props.genderList(`pagination=false&onlyExistingProducts=true&retailer=${retailer.split("/retailers/").pop()}`);
        } else if ('Category' === value && !this.props.categoryRetrieved) {
            this.props.categoryList(`pagination=false&onlyExistingProducts=true&retailer=${retailer.split("/retailers/").pop()}`);
        }
    }

    renderFilterElement = (filters) => {
        const {classes} = this.props;
        const {filterMenuSelected} = this.state;

        if (filterMenuSelected === "Price") {
            return this.renderFilterPriceElement();
        } else if (filterMenuSelected === "StatusStock") {
            return this.renderFilterStatusStockElement(filters[filterMenuSelected]);
        } else if (!filters[filterMenuSelected]) {
            return (
                <div className={classes.filterLoading}>
                    <BeatLoader
                        sizeUnit={"px"}
                        size={12}
                        color={primaryColor}
                        loading={true}
                    />
                </div>
            )
        } else if (filterMenuSelected === "Category") {
            return this.renderFilterCategoriesContainer();
        } else if (filters[filterMenuSelected]) {
            if (VariationFilter.lock.type === filterMenuSelected) {
                // no sort
                filters[filterMenuSelected] = filters[filterMenuSelected]
            } else {
                filters[filterMenuSelected].sort(sortOn("value"))
            }

            return filters[filterMenuSelected].map(element => {
                let name = '';
                if (['Brand', VariationFilter.lock.type].includes(filterMenuSelected)) {
                    name = element.name;
                } else if ('Gender' === filterMenuSelected) {
                    name = getTranslation(element).value;
                } else {
                    name = getTranslation(element).name;
                }
                return <Chip
                    label={name}
                    key={element.id}
                    onClick={() => this.handleAddSelectedFilter(element)}
                    variant={this.checkIfFilterExist(element) ? "default" : "outlined"}
                    classes={{root: classes.filterItem}}
                />
            })
        }
    };

    renderFilterStatusStockElement = (stockStatusFilter) => {
        const {classes} = this.props;

        return (
            <div className={classes.statusStockFilter}>
                {stockStatusFilter.map(filter => {
                    let filterTitle = Object.keys(filter)[0];

                    return (
                        <div className={classes.filterInColumn} key={filterTitle}>
                            <span className={classes.filterBlueTitle}>
                                {this.props.intl.formatMessage({id: "retailer.product.list.filter." + filterTitle.toLowerCase()})}
                            </span>
                            {filter[filterTitle].map(element => {
                                return <Chip
                                    label={element.name || element.value}
                                    key={element.id}
                                    onClick={() => this.handleAddSelectedFilter(element)}
                                    variant={this.checkIfFilterExist(element) ? "default" : "outlined"}
                                    classes={{root: classes.filterItem}}
                                />
                            })}
                        </div>
                    );
                })}
            </div>
        );
    };

    getCategoryChildren = (parentId, childToFindId, indexChildToDelete = -1) => {
        const {categoryRetrieved} = this.props;

        categoryRetrieved && categoryRetrieved.filter(cat => cat.parent && cat.parent.id === parentId).forEach(child => {
            if (child.id === childToFindId) {
                indexChildToDelete = this.state.selectedFilters.indexOf(child);
            }
            else {
                indexChildToDelete = this.getCategoryChildren(child.id, childToFindId, indexChildToDelete);
            }
        });

        return indexChildToDelete;
    };

    handleAddCategorySelectedFilter = (category, index) => {
        const {selectedFilters} = this.state;
        let indexToDelete = [];

        selectedFilters.forEach(filter => {
            // if selected fitlers list contain the parent of the category clicked
            // delete the parent from filter and replace by the category clicked
            if (category.parent && category.parent.id === filter.id) {
                let indexParentToDelete = selectedFilters.indexOf(filter);

                if (indexParentToDelete > -1) {
                    indexToDelete.push(indexParentToDelete);
                }
            }

            // if selected filters list contain children
            // find if theses children are children of the category clicked
            // and then delete them and replace by the category clicked
            else {
                if (filter.parent && filter.parent !== null ) {
                    let indexChildToDelete;

                    // if the filter is a child of the category clicked
                    if (filter.parent.id === category.id) {
                        indexChildToDelete = selectedFilters.indexOf(filter);
                    } else {
                        // get children of the categoruy until match with the filter
                        indexChildToDelete = this.getCategoryChildren(category.id, filter.id);
                    }

                    if (indexChildToDelete > -1) {
                        indexToDelete.push(indexChildToDelete);
                    }
                }
            }
        });

        // get all index of filters and delete them
        for (let i = indexToDelete.length -1; i >= 0; i--) {
            selectedFilters.splice(indexToDelete[i],1);
        }

        this.setState({selectedFilters}, () => {
            this.handleAddSelectedFilter(category);
            this.handleShowChildrenFilterColumn(category.id, index)
        });
    };

    handleShowChildrenFilterColumn = (id,index) => {
        const {childrenColumns} = this.state;

        childrenColumns[index] = id;

        if (this.hasChildren(id) && childrenColumns[index + 1] !== null) {
            childrenColumns.length = index +1
        } else {
            childrenColumns.length = index;
        }
        this.setState({childrenColumns});
    };

    renderFilterCategoriesElement = (childs,index) => {
        const {classes} = this.props;

        return childs.map(category => {
            return (
                <Chip
                    label={getTranslation(category).name}
                    key={category.id}
                    onClick={() => this.handleAddCategorySelectedFilter(category, index + 1)}
                    variant={this.checkIfFilterExist(category) ? "default" : "outlined"}
                    classes={{root: classes.filterItem}}
                />
            )
        })
    };

    getCategoryParent = (category) => {
        if (category.parent) {
            return this.getCategoryParent(category.parent);
        } else {
            return category;
        }
    }

    renderFilterCategoriesContainer = () => {
        const {classes,categoryRetrieved} = this.props;

        let categoryParentsFromRetrieved = [];
        let categoryParents = [];
        let alreadyListedCategory = [];
        if (categoryRetrieved) {
            categoryRetrieved.forEach(category => {
                let parent = this.getCategoryParent(category);
                categoryParentsFromRetrieved.push(parent);
            });
            categoryParentsFromRetrieved.forEach(element => {
                if (alreadyListedCategory.indexOf(element.id) < 0) {
                    categoryParents.push(element);
                    alreadyListedCategory.push(element.id);
                }
            });
        }

        return (
            <div className={classes.categoriesFilterContainer}>
               <div className={classes.categoriesFilterColumn}>
                    {categoryRetrieved && categoryParents.map(category => {
                        return (
                            <Chip
                                label={getTranslation(category).name}
                                key={category.id}
                                onClick={() => this.handleAddCategorySelectedFilter(category, 0)}
                                variant={this.checkIfFilterExist(category) ? "default" : "outlined"}
                                classes={{root: classes.filterItem}}
                            />
                        )
                    })}
                </div>

                {categoryRetrieved && this.state.childrenColumns.map((id,index) => {
                    return(
                        <div key={id} className={classes.categoriesFilterColumn}>
                            {this.renderFilterCategoriesElement(categoryRetrieved.filter(cat => cat.parent && cat.parent.id === id), index)}
                        </div>
                    );
                })}
            </div>
        );
    };

    renderFilterPriceElement = () => {
        const {minPriceFilter, maxPriceFilter} = this.state;
        const {classes} = this.props;

        return (
            <div className={classes.priceFilter}>
                <div>
                    <TextField
                        type="number"
                        value={minPriceFilter}
                        onChange={(event) => this.setState({minPriceFilter: event.target.value})}
                        InputLabelProps={{shrink: true}}
                        variant="outlined"
                    />
                    <span className={classes.priceFilterTo}>à</span>
                    <TextField
                        type="number"
                        value={maxPriceFilter}
                        onChange={(event) => this.setState({maxPriceFilter: event.target.value})}
                        InputLabelProps={{shrink: true}}
                        variant="outlined"
                    />
                    <Button color={"info"} onClick={() => this.handlePriceFilter()}
                            className={classes.priceFilterSearch}>
                        <FormattedMessage id={"retailer.product.list.filter.price.search"}/>
                    </Button>
                </div>
            </div>
        );
    }

    renderSelectedFilter = () => {
        const {selectedFilters} = this.state;
        const {intl} = this.props;

        const filters = selectedFilters.map(element => {
            if (element["@type"] === "Price") {
                return {
                    key: "Price",
                    element: element,
                    label: intl.formatMessage(
                        {id: "retailer.product.list.filter.selected.price"},
                        {min: element.minPrice, max: element.maxPrice, currency: this.props.currentOrganization?.currency}
                    ),
                };
            }

            let name = element.name;
            if (!element.name && 'Gender' === element["@type"]) {
                name = getTranslation(element).value;
            } else if (!element.name) {
                name = getTranslation(element).name;
            }

            const typeLabel = getFilterTypeLabel(element, intl);

            return {
                key: element.id,
                element: element,
                label: typeLabel ? `${typeLabel} : ${name}` : name,
            };
        });

        return (
            <ListingActiveFiltersBar
                filters={filters}
                onRemove={(element) => this.handleDeleteSelectedFilter(element)}
                onResetAll={() => this.setState({selectedFilters: []}, () => this.handleRouteReset())}
            />
        );
    };

    renderActions = () => {
        const {classes} = this.props;

        return (
            <div className={classes.actionsContainer}>
                {this.renderSelectableActions()}
                {this.renderAddCategories()}
                {this.props.categoryRetrieved && this.renderDeleteCategories()}
                {this.renderEditGender()}
                {this.renderEditBrand()}
                {this.renderDeleteProducts()}
                {this.renderSortButton()}
                {this.renderExportButton()}
            </div>
        );
    };

    renderExportButton() {
        const { classes, exportLoading, exportSuccess } = this.props;

        const handleSubmit = () => {
            this.props.export(this.props.retailer, this.props.match.params.params)
        };

        if (this.props.loading || UserAgentUtils.isMobile()) {
            return null;
        }

        if (exportLoading) {
            return (
                <BeatLoader
                    sizeUnit={"px"}
                    size={12}
                    color={primaryColor}
                    loading={true}
                />
            )
        }

        return (
            <ListingExportButton
                tooltip={this.props.intl.formatMessage({id: "retailer.product.list.export"})}
                onClick={handleSubmit}
                successMessage={exportSuccess && <FormattedMessage id={"retailer.product.list.export.success"} />}
            />
        );
    }

    renderSelectableActions = () => {
        if (UserAgentUtils.isMobile()) {
            return;
        }

        const {classes} = this.props;
        const {selected, action} = this.state;

        const isActionsDisable = (!this.state.isTotalSelected && this.state.selected.length < 1) || this.props.massUpdateLoading || this.state.loadingAllProduct;

        if (this.props.offer === stockEasyOffer) {
            const tooltip = isActionsDisable ?
                'retailer.product.list.action.mass_product_status_toggle_disable' :
                'retailer.product.list.action.mass_product_status_toggle_explanation';
            return (
                <div className={classes.disableElementsContainer}>
                    <span className={classes.myOffers}>
                        <FormattedMessage  id={"retailer.product.list.action.products"} />
                    </span>
                    <Tooltip title={<FormattedMessage id={tooltip}/>}>
                        <ButtonGroup variant="text" color="primary" aria-label="text primary button group">
                            <Button
                                color={"danger"}
                                type="submit"
                                disabled={isActionsDisable}
                                onClick={() => this.handleEditStatusToSelected(false)}
                            >
                                <FormattedMessage id={"retailer.product.list.action.stockeasy.disable"} />
                            </Button>

                            <Button
                                color={"success"}
                                type="submit"
                                disabled={isActionsDisable}
                                onClick={() => this.handleEditStatusToSelected(true)}
                            >
                                <FormattedMessage id={"retailer.product.list.action.stockeasy.activate"} />
                            </Button>
                        </ButtonGroup>
                    </Tooltip>
                </div>
            );
        } else {
            return (
                <ListingActionsSelect
                    value={action}
                    onChange={this.handleChangeAction}
                    displayEmpty={true}
                    autoWidth={true}
                    disabled={this.state.selected.length < 1 || this.props.massUpdateLoading || this.state.loadingAllProduct}
                >
                    <MenuItem value="" disabled>Actions</MenuItem>
                    <MenuItem value={"active"}>
                        <FormattedMessage id={"retailer.product.list.action.offer.activate"}/>
                    </MenuItem>
                    <MenuItem value={"inactive"}>
                        <FormattedMessage id={"retailer.product.list.action.offer.disable"}/>
                    </MenuItem>
                    {this.renderMenuItems("addCategories","retailer.product.list.action.add.categories","retailer.product.list.action.add.categories.empty")}
                    {this.renderMenuItems("deleteCategories","retailer.product.list.action.delete.categories","retailer.product.list.action.add.categories.empty")}
                    {this.renderMenuItems("editGender","retailer.product.list.action.gender","retailer.product.list.action.gender.empty")}
                    {this.renderMenuItems("editBrand","retailer.product.list.action.brand","retailer.product.list.action.brand.empty")}
                    {hasAuthorization(this.props.authorizations, Authorizations.MARKETPLACE_PRODUCT_MANAGEMENT) &&
                    <MenuItem value={"deleteProducts"} className={"destructive"}>
                        <FormattedMessage id={"retailer.product.list.action.delete.variations"}/>
                    </MenuItem>}
                </ListingActionsSelect>
            );
        }
    };

    renderMenuItems = (value, message, tooltip) => {
        return <MenuItem value={value}>
            <FormattedMessage id={message}/>
        </MenuItem>
    };

    handleShowChildren = (categoryId) => {
        const {showChildren} = this.state;

        showChildren[categoryId] = !showChildren[categoryId];

        this.setState({showChildren});
    };

    handleDeleteSelectedCategory = (category) => {
        const {categoriesToAdd} = this.state;

        let index = categoriesToAdd.indexOf(category);

        categoriesToAdd.splice(index, 1);
        this.setState({categoriesToAdd});
    };

    hasChildren = (parentId) => {
        const {categoryRetrieved} = this.props;

        if (categoryRetrieved && categoryRetrieved.filter(cat => cat.parent && cat.parent.id === parentId).length > 0) {
            return true;
        }
    };

    renderCategoryChildren = (categories, checkChildren = false) => {
        const {categoryRetrieved, classes} = this.props;
        const {categoriesToAdd, showChildren} = this.state;

        if (categoryRetrieved && categories.length > 0) {
            return categories.map(category => {
                let hasChildren = this.hasChildren(category.id);
                let isChecked = categoriesToAdd.indexOf(category) !== -1 || checkChildren;

                return (
                    <div key={category.id}>
                        <ListItem  className={classes.categorieListItem} button={hasChildren}>
                            <Checkbox
                                color="primary"
                                onChange={() => this.handleCheckCategoryAdd(category, categoriesToAdd)}
                                checked={isChecked}
                                classes={{checked: classes.checked}}
                                disabled={hasChildren}
                            />
                            <ListItemText
                                className={classes.categoryNameListItem + ' '+((showChildren[category.id] || categoriesToAdd.indexOf(category) !== -1) ? classes.categoryNameListItemSelected : null)}
                                primary={category.name}
                                onClick={() => hasChildren ? this.handleShowChildren(category.id) : this.handleCheckCategoryAdd(category, categoriesToAdd)}
                            />
                            {hasChildren && (showChildren[category.id] ? <KeyboardArrowDown/> : <KeyboardArrowRight/>)}
                        </ListItem>
                        <Collapse className={classes.childrenCategories} in={hasChildren && showChildren[category.id]} timeout="auto" unmountOnExit>
                            {this.renderCategoryChildren(categoryRetrieved.filter(cat => cat.parent !== null && cat.parent.id === category.id))}
                        </Collapse>
                    </div>
                );
            })
        } else {
            return <div className={classes.tableCellNoResult}><FormattedMessage id={"retailer.list.table.no_result"}/></div>
        }
    };

    renderAddCategories = () => {
        const {classes} = this.props;
        const {categoriesToAdd, addCategoriesDialog, searchCategories, allCategories, allCategoriesFetched} = this.state;

        return (
            <Dialog
                scroll="paper"
                open={addCategoriesDialog}
                onClose={() => {this.handleDialog('addCategoriesDialog');}}
                className={classes.dialog}
                fullWidth
            >
                <DialogTitle className={classes.dialogTitle}>
                    <FormattedMessage id={"retailer.product.list.action.dialog.title.add.categories"}/>
                </DialogTitle>
                <Divider/>
                <DialogContent className={classes.dialogContent}>
                    { allCategoriesFetched ? (
                        <>
                            <Input
                                placeholder={this.props.intl.formatMessage({id: "retailer.product.list.action.add.categories.find"})}
                                onChange={(e) => this.setState({searchCategories: e.target.value})}
                                className={classes.dialogSearch}
                                autoFocus
                                disableUnderline
                            />
                            <Divider/>
                            <List disablePadding className={classes.dialogContentChild}>
                                {searchCategories.length > 0 ?
                                    this.renderCategoryChildren(allCategories.filter(cat => cat.name.toLowerCase().includes(searchCategories)))
                                :
                                    this.renderCategoryChildren(allCategories.filter(cat => cat.parent === null))
                                }
                            </List>
                        </>
                    ) : (
                        <div className={classes.dialogContentLoader}>
                            <BeatLoader
                                sizeUnit={"px"}
                                size={12}
                                color={primaryColor}
                                loading={true}
                            />
                        </div>
                    )}
                </DialogContent>
                { allCategoriesFetched && (
                    <>
                        <Divider/>
                        <div>
                            {categoriesToAdd.length > 0 && categoriesToAdd.map(category => {
                                return <Chip
                                    key={category.id}
                                    label={category.name || category.value}
                                    color="primary"
                                    onDelete={() => this.handleDeleteSelectedCategory(category)}
                                    className={classes.filterSelectedItem}
                                />
                              })}
                        </div>
                        <Divider/>
                        <DialogActions>
                            <Button color={"info"} simple onClick={() => this.handleDialog('addCategoriesDialog')} >
                                <ArrowBackIos/>
                                <FormattedMessage id={"retailer.product.list.dialog.cancel"}/>
                            </Button>
                            <Button
                                round
                                onClick={() => this.handleAddCategoriesToSelected()}
                                color="primary"
                            >
                                <FormattedMessage id={"retailer.product.list.dialog.add"}/>
                            </Button>
                        </DialogActions>
                    </>
                )}
            </Dialog>
        );
    };

    renderDeleteProducts = () => {
        const {classes} = this.props;
        const {deleteProductsDialog} = this.state;

        return (
            <Dialog
                scroll="paper"
                open={deleteProductsDialog}
                onClose={() => this.handleDialog('deleteProductsDialog')}
                fullWidth
            >
                <DialogTitle className={classes.dialogTitle}>
                    <FormattedMessage id={"retailer.product.list.action.delete.variations"}/>
                </DialogTitle>
                <Divider/>
                <DialogContent className={classes.dialogTitle}>
                    <FormattedMessage id={"retailer.product.list.action.delete.variations.notice"}/>
                </DialogContent>
                <DialogActions>
                    <Button color={"info"} simple onClick={() => this.handleDialog('deleteProductsDialog')}>
                        <ArrowBackIos/>
                        <FormattedMessage id={"retailer.product.list.dialog.cancel"}/>
                    </Button>
                    <Button
                        round
                        onClick={() => this.handleDeleteProductsToSelected()}
                        color="danger"
                    >
                        <Delete/>
                        <FormattedMessage id={"retailer.product.list.dialog.action.delete"}/>
                    </Button>
                </DialogActions>
            </Dialog>
        );
    };

    renderDeleteCategories = () => {
        const {classes} = this.props;
        const {categoriesToDelete, deleteCategoriesDialog, selected} = this.state;

        return (
            <Dialog
                scroll="paper"
                open={deleteCategoriesDialog}
                onClose={() => this.handleDialog('deleteCategoriesDialog')}
                fullWidth
            >
                <DialogTitle className={classes.dialogTitle}>
                    <FormattedMessage id={"retailer.product.list.action.delete.categories"}/>
                </DialogTitle>
                <Divider/>
                <DialogContent className={classes.dialogTitle}>
                    <List disablePadding>
                        {this.getCategoriesByVariations(selected).map(cat => {
                            return(
                                <ListItem key={cat.id} className={classes.categorieListItem}>
                                    <Checkbox
                                        color="primary"
                                        onChange={() => this.handleCheckCategoryDelete(cat,categoriesToDelete)}
                                        checked={categoriesToDelete.indexOf(cat) === -1}
                                        classes={{checked: classes.checked}}
                                    />
                                    <ListItemText primary={getTranslation(cat).name} />
                                </ListItem>
                            );
                        })}
                    </List>
                </DialogContent>
                <DialogActions>
                    <Button color={"info"} simple onClick={() => this.handleDialog('deleteCategoriesDialog')}>
                        <ArrowBackIos/>
                        <FormattedMessage id={"retailer.product.list.dialog.cancel"}/>
                    </Button>
                    <Button
                        disabled={!categoriesToDelete.length}
                        round
                        onClick={() => this.handleDeleteCategoriesToSelected()}
                        color="primary"
                    >
                        <FormattedMessage id={"retailer.product.list.dialog.submit"}/>
                    </Button>
                </DialogActions>
            </Dialog>
        );
    };

    renderEditGender = () => {
        const {classes} = this.props;
        const {genderDialog,genderSelected,allGendersFetched} = this.state;
        const genderRetrieved = this.state.allGenders;

        return (
            <Dialog
                open={genderDialog}
                scroll="paper"
                onClose={() => this.handleDialog("genderDialog")}
                fullWidth
            >
                <DialogTitle className={classes.dialogTitle}>
                    <FormattedMessage id={"retailer.product.list.action.gender"}/>
                </DialogTitle>
                <DialogContent className={classes.dialogContent}>
                    { allGendersFetched ? (
                        <Select
                            value={genderSelected.length > 0 ? genderSelected : (genderRetrieved.length > 0 ? genderRetrieved[0]['@id'] : '')}
                            onChange={(event) => this.setState({genderSelected: event.target.value})}
                            input={<InputBase value={this.state.genderSelected}/>}
                            IconComponent={UnfoldMore}
                            className={classes.inviteSelectField}
                            variant="outlined"
                        >
                            {genderRetrieved.map(item => {
                                return <MenuItem key={item.id}
                                                 value={item['@id']}>{getTranslation(item).value}</MenuItem>
                            })}
                        </Select>
                    ) : (
                        <div className={classes.dialogContentLoader}>
                            <BeatLoader
                                sizeUnit={"px"}
                                size={12}
                                color={primaryColor}
                                loading={true}
                            />
                        </div>
                    )}
                </DialogContent>
                { allGendersFetched && (
                    <DialogActions>
                        <Button color={"info"} simple onClick={() => this.handleDialog('genderDialog')}>
                            <ArrowBackIos/>
                            <FormattedMessage id={"retailer.product.list.dialog.cancel"}/>
                        </Button>
                        <Button
                            round
                            onClick={() => this.handleEditGenderToSelected(genderSelected)}
                            color="primary"
                        >
                            <FormattedMessage id={"retailer.product.list.dialog.submit"}/>
                        </Button>
                    </DialogActions>
                )}
            </Dialog>
        );
    };

    renderEditBrand = () => {
        const {classes} = this.props;
        const {brandDialog,brandSelected, searchBrand, allBrandsFetched} = this.state;
        const brandRetrieved = this.state.allBrands;

        let brandArray =  searchBrand.length > 0 ? brandRetrieved.filter(brand => brand.name.toLowerCase().includes(searchBrand)) : brandRetrieved;

        return (
            <Dialog
                open={brandDialog}
                scroll="paper"
                onClose={() => {
                    this.handleDialog("brandDialog");
                    this.setState({searchBrand: ''});
                }}
                fullWidth
            >
                <DialogTitle className={classes.dialogTitle}>
                    <FormattedMessage id={"retailer.product.list.action.brand"}/>
                </DialogTitle>
                <Divider/>
                <DialogContent className={classes.dialogContent}>
                    { allBrandsFetched ? (
                        <>
                            <Input
                                placeholder={this.props.intl.formatMessage({id: "retailer.product.list.action.brand.find"})}
                                onChange={(e) => this.setState({searchBrand: e.target.value})}
                                className={classes.dialogSearch}
                                autoFocus
                                disableUnderline
                            />
                            <Divider/>
                            {brandArray.length > 0 ?
                                <RadioGroup
                                    ref={null}
                                    aria-label="Brand"
                                    name="brand"
                                    value={brandSelected.length > 0 ? brandSelected : brandArray[0]["@id"]}
                                    onChange={(event) => this.setState({brandSelected: event.target.value}) }
                                    className={classes.dialogContentChild}
                                >
                                {brandArray.map(brand => (
                                    <FormControlLabel value={brand["@id"]} key={brand.id} control={<Radio color="primary" />} label={brand.name} />
                                ))}
                                </RadioGroup>
                                :
                                <div className={classes.tableCellNoResult}><FormattedMessage id={"retailer.list.table.no_result"}/></div>
                            }
                        </>
                    ) : (
                        <div className={classes.dialogContentLoader}>
                            <BeatLoader
                                sizeUnit={"px"}
                                size={12}
                                color={primaryColor}
                                loading={true}
                            />
                        </div>
                    )}
                </DialogContent>
                { allBrandsFetched && (
                    <DialogActions>
                        <Button
                            simple
                            onClick={() => {
                                this.handleDialog("brandDialog");
                                this.setState({searchBrand: ''});
                            }}
                            color="info">
                            <ArrowBackIos/>
                            <FormattedMessage id={"retailer.product.list.dialog.cancel"}/>
                        </Button>
                        <Button
                            round
                            onClick={() => this.handleEditBrandToSelected(brandSelected)}
                            color="primary"
                        >
                            <FormattedMessage id={"retailer.product.list.dialog.submit"}/>
                        </Button>
                    </DialogActions>
                )}
            </Dialog>
        );
    };

    renderNotification = () => {
        const {massUpdateSuccess, massUpdateError} = this.props;
        const {showNotification} = this.state;

        if (massUpdateSuccess) {
            return (
                <Snackbar
                    open={showNotification}
                    close
                    closeNotification={() => this.setState({showNotification: false})}
                    place={"bl"}
                    color={"success"}
                    icon={function () {
                        return <NotificationsActive/>
                    }}
                    message={<FormattedMessage id={"retailer.product.list.notifications.list.updated"}/>}
                />
            );
        } else if (massUpdateError) {
            return (
                <Snackbar
                    open={showNotification}
                    close
                    closeNotification={() => this.setState({showNotification: false})}
                    place={"bl"}
                    color={"danger"}
                    icon={function () {
                        return <NotificationsActive/>
                    }}
                    message={massUpdateError}
                />
            );
        }
    };

    renderLoadingBar = () => {
        const {loading, massUpdateLoading, retrieved, classes} = this.props;

        // the first load shows the skeleton rows instead of a loader
        if (!retrieved) {
            return null;
        }

        return (
            <div className={classes.beatLoader}>
                <BeatLoader
                    sizeUnit={"px"}
                    size={12}
                    color={primaryColor}
                    loading={loading || massUpdateLoading}
                />
            </div>
        )
    };

    isSelected = item => this.state.selected.some(selectedItem => selectedItem.id === item.id);

    getParameterNameAndPropertyValue = (params, paramName) => {
        let values = "";
        params = params.split("&");
        params.map((param) => {
            if (param.search(paramName) !== -1)
                values = param;
        });
        return values;
    };

    handleEditVariation = (event, variation, currentPage, currentOrderBy) => {
        if (window.getSelection().toString().length > 0) {
            return;
        }

        if (!hasProductEditionAuthorization(this.props.authorizations)) {
            return;
        }

        const url = `${this.state.baseParamsUrl + this.props.match.params.id}/products/show/${variation.product.id}/variation/${variation.id}`;
        if (event.metaKey || event.ctrlKey) {
            window.open(url, '_blank');
        } else {
            this.props.history.push({
                pathname: url,
                currentPage: currentPage,
                currentOrderBy: currentOrderBy,
            });
        }
    }

    removeEikowAndFeedProductsFromList = (variationList) => {
        return variationList.filter(variation => variation.product.eikow === false);
    }


    render() {
        const {classes, retrieved, intl} = this.props;
        const {itemsPerPage, selectedFilters} = this.state;
        const currentPage = this.getParameterNameAndPropertyValue(decodeURIComponent(this.props.match.params.params), "page");
        const currentOrderBy = this.getParameterNameAndPropertyValue(decodeURIComponent(this.props.match.params.params), "order");

        return (
            <div className={classes.containerWithSidebar}>
                <div className={classes.container}>
                    <GridContainer>
                        <GridItem xs={12}>
                            <Card className={classes.card}>
                                <CardHeader color="info">
                                    <ListingProductPageHeader
                                        titleId={"retailer.product.list.variation.title"}
                                        switchTooltipId={"retailer.product.icon.list.simple"}
                                        onSwitchView={() => this.props.history.push(`${this.state.baseParamsUrl + this.props.match.params.id}/products/${encodeURIComponent([`retailer.id=${decodeURIComponent(this.props.match.params.id).replace("/retailers/", "")}`, 'order[id]=asc', ...defaultProductListFilters(this.props.offer), 'itemsPerPage=50', 'page=1'].join("&"))}`)}
                                        merchantName={this.props.retailer?.name}
                                        retailerId={decodeURIComponent(this.props.match.params.id).replace("/retailers/", "")}
                                        searchValue={this.state.name ?? ''}
                                        searchPlaceholderId={"retailer.product.list.filter.search.placeholder.variation"}
                                        onSearchChange={(event) => this.handleSearch(event)}
                                    />
                                </CardHeader>
                                <CardBody>
                                    {this.props.offer !== stockEasyOffer && !this.isCatalogEmpty() &&
                                        <FilterBlock footer={selectedFilters.length > 0 ? this.renderSelectedFilter() : null}>
                                            {this.renderFilterMenu()}
                                        </FilterBlock>
                                    }
                                    <GridContainer>
                                        <GridItem xs={12} sm={6} className={classes.action}>
                                            {hasProductEditionAuthorization(this.props.authorizations) && this.renderActions()}
                                        </GridItem>
                                        <GridItem xs={12} sm={6} className={classes.totalContainer}>
                                            <ListingTotalCount
                                                labelId={"retailer.product.list.label.totalProduct"}
                                                total={retrieved ? retrieved["hydra:totalItems"] : null}
                                            >
                                                <ItemsPerPageSelect
                                                    prefixUrl={`${this.state.baseParamsUrl + this.props.match.params.id}/products/variations/`}
                                                    history={this.props.history}
                                                    match={this.props.match}
                                                    defaultItemsPerPage={"50"}
                                                />
                                            </ListingTotalCount>
                                        </GridItem>
                                    </GridContainer>
                                    {this.renderSelectTotal()}
                                    <div className={classes.tableWrapper}>
                                        {this.isCatalogEmpty() ?
                                        <ListingEmptyState
                                            titleId={"retailer.product.list.empty.title"}
                                            descriptionId={"retailer.product.list.empty.description"}
                                        />
                                        :
                                        <>
                                        <Table padding={retrieved && retrieved["hydra:member"].length > 0 ? "none" : "normal"}>
                                            <TableHead
                                                onSelectAllClick={this.handleSelectAllClick}
                                                isSelectAllChecked={this.state.isSelectAllChecked}
                                                isRetrievedFinished={retrieved && retrieved["hydra:member"].length > 0}
                                                showCheckbox={hasProductEditionAuthorization(this.props.authorizations)}
                                                tableHead={[
                                                    {id: "photo", numeric: false, disablePadding: true, label: this.props.intl.formatMessage({id: "retailer.product.list.header.photo"})},
                                                    {id: "article", numeric: false, disablePadding: true, label: this.props.intl.formatMessage({id: "retailer.product.list.header.article"})},
                                                    {id: "outletPrice", numeric: true, disablePadding: true, label: this.props.intl.formatMessage({id: "retailer.product.list.header.price"}), hideOnMobile: true},
                                                    {id: "sales", numeric: true, disablePadding: true, label: this.props.intl.formatMessage({id: "retailer.product.list.header.sales"}), hideOnMobile: true},
                                                    {id: "stock", numeric: true, disablePadding: true, label: this.props.intl.formatMessage({id: "retailer.product.list.header.stock.status"}), hideOnMobile: true},
                                                ]}
                                            />
                                            <TableBody>
                                                {!retrieved && this.props.loading &&
                                                    <ListingRowsSkeleton withScoring={false}/>
                                                }
                                                {!this.props.massUpdateLoading && (retrieved && retrieved["hydra:member"].length > 0) &&
                                                retrieved["hydra:member"].map(variation => {
                                                    const isSelected = this.isSelected(variation);
                                                    return (
                                                        <TableRow
                                                            onClick={(e) => this.handleEditVariation(e, variation, currentPage, currentOrderBy)}
                                                            hover
                                                            tabIndex={-1}
                                                            key={variation.id}
                                                            aria-checked={isSelected}
                                                            selected={isSelected}
                                                            classes={{hover: hasProductEditionAuthorization(this.props.authorizations) && classes.tableRow}}
                                                        >
                                                            <ListingTableCell>
                                                                <ImageAndCheckbox>
                                                                    {/* The header always shows a checkbox, so a row that cannot
                                                                        be selected keeps its slot rather than sliding its
                                                                        content 24px left of the column label. */}
                                                                    {!UserAgentUtils.isMobile() && hasProductEditionAuthorization(this.props.authorizations) && (
                                                                        (false === variation.product.eikow || this.props.offer !== stockEasyOffer)
                                                                            ? <ListingCheckbox
                                                                                checked={isSelected}
                                                                                onClick={(e) => {
                                                                                    e.stopPropagation();
                                                                                    this.handleSelectProduct(variation)
                                                                                }}
                                                                            />
                                                                            : <ListingCheckboxPlaceholder/>
                                                                    )}
                                                                    <ListingProductImageCell
                                                                        src={variation.pictures.length > 0 ? getFirstProductImage(variation.pictures) : null}
                                                                        alt="variation_image"
                                                                        groupedSearchImpression={variation.groupedSearchImpression}
                                                                    />
                                                                </ImageAndCheckbox>
                                                            </ListingTableCell>
                                                            <ListingTableCell>
                                                                <Hidden smDown>
                                                                    {this.renderArticleCell(variation)}
                                                                </Hidden>
                                                                <Hidden smUp>
                                                                    {this.renderMobileArticleCell(variation)}
                                                                </Hidden>
                                                            </ListingTableCell>
                                                            <Hidden smDown>
                                                                <ListingTableCell>
                                                                    {this.renderPriceCell(variation)}
                                                                </ListingTableCell>
                                                            </Hidden>
                                                            <Hidden smDown>
                                                                <ListingTableCell>
                                                                    <ListingSalesCell
                                                                        onlineSales={variation.onlineSales}
                                                                        totalSales={computeTotalSales(variation)}
                                                                    />
                                                                </ListingTableCell>
                                                            </Hidden>
                                                            <Hidden smDown>
                                                                <ListingTableCell>
                                                                    {this.renderStockCell(variation)}
                                                                </ListingTableCell>
                                                            </Hidden>
                                                        </TableRow>
                                                    );
                                                })
                                                }
                                                {retrieved && retrieved["hydra:member"].length === 0 &&
                                                <TableRow>
                                                    <TableCell colSpan={5} sx={{padding: '0 !important'}}>
                                                        <ListingEmptyState
                                                            titleId={"retailer.product.list.noResult.title"}
                                                            descriptionId={"retailer.product.list.noResult.description"}
                                                            ctaLabelId={"retailer.product.list.noResult.cta"}
                                                            ctaVariant={"secondary"}
                                                            onCtaClick={() => this.setState({selectedFilters: []}, () => this.handleRouteReset())}
                                                        />
                                                    </TableCell>
                                                </TableRow>
                                                }
                                            </TableBody>
                                        </Table>
                                        {!this.props.massUpdateLoading && retrieved &&
                                            <ListingPagination
                                                currentPage={parseInt((currentPage || "").split("=")[1]) || 1}
                                                totalPages={Math.ceil(retrieved["hydra:totalItems"] / this.state.itemsPerPage)}
                                                onPageChange={this.handlePageChange}
                                            />
                                        }
                                        </>
                                        }
                                    </div>
                                </CardBody>
                                {this.renderLoadingBar()}
                            </Card>
                        </GridItem>
                    </GridContainer>
                </div>
                {this.renderNotification()}
            </div>
        );
    }
};

EnhancedTable.propTypes = {
    classes: PropTypes.object.isRequired,
};

const mapStateToProps = state => {
    return {
        retrieved: state.variation.list.retrieved,
        brandRetrieved: state.brand.list.retrieved,
        categoryRetrieved: state.category.list.retrieved,
        genderRetrieved: state.gender.list.retrieved,
        loading: state.variation.list.loading,
        exportLoading: state.variation.list.exportLoading,
        exportSuccess: state.variation.list.exportSuccess,
        massUpdateLoading : state.product.massUpdate.massUpdateLoading,
        massUpdateSuccess : state.product.massUpdate.massUpdateSuccess,
        massUpdateError: state.product.massUpdate.massUpdateError,
        member: state.authentication.member,
        authorizations: state.authentication.authorizations,
        itemsPerPage: state.itemsPerPage.itemsPerPage,
        retailer: state.retailer.show.retrieved,
        currentOrganization: state.currentOrganization.retrieved ?? null,
    };
};

const mapDispatchToProps = dispatch => ({
    list: (params) => dispatch(list(params)),
    export: (retailer, params) => dispatch(downloadExport(retailer, params)),
    reset: () => dispatch(reset()),
    brandList: params => dispatch(brandList(params)),
    categoryList: params => dispatch(categoryList(params)),
    genderList: params => dispatch(genderList(params)),
    massUpdate: (params, values) => dispatch(massUpdate(params, values)),
    massUpdateReset: eventSource => dispatch(massUpdateReset(eventSource)),
    retailerOptionValuesList: params => dispatch(retailerOptionValuesList(params)),
});

export default connect(
    mapStateToProps,
    mapDispatchToProps
)(withStyles(listStyle)(injectIntl(EnhancedTable)));
